import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import MUITable, { TableProps as MUITableProps } from '@mui/material/Table'
import MUITableBody, { TableBodyProps } from '@mui/material/TableBody'
import MUITableCell, { TableCellProps } from '@mui/material/TableCell'
import MUITableContainer, { TableContainerProps } from '@mui/material/TableContainer'
import MUITableHead, { TableHeadProps } from '@mui/material/TableHead'
import MUITableRow, { TableRowProps } from '@mui/material/TableRow'
import { Children, isValidElement, ReactElement, ReactNode, useEffect, useState } from 'react'

import EmptyTable from '../images/empty-table.png'
import { Text } from '../index'
import { LuneThemeNew } from '../theme'
import { staticImagePath } from '../utils'

const { palette } = LuneThemeNew

export type TableProps = MUITableProps & {
    headerCells?: ReactElement
    bodyCells?: ReactElement
    footer?: ReactNode
    loading?: boolean
    empty?: boolean
    emptyStateText?: {
        title: ReactNode
        description: ReactNode
    }
    withSelectCheckbox?: boolean
    /**
     * Make the footer sticky.
     * @default false
     */
    stickyFooter?: boolean
}

export interface CellProps extends TableCellProps {
    disabled?: boolean
    shrink?: boolean
    alignEnd?: boolean
    colSize?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
    /**
     * If true, wraps and truncates text in one line
     * Defaults to false (multi-line).
     */
    oneLineText?: boolean
    /**
     * If true, reduces the cell height to 56px for a more compact layout
     * Defaults to false (64px height).
     */
    compact?: boolean
}

export interface ExtendedTableHeadProps extends TableHeadProps {
    withSelectCheckbox?: boolean
}

const TableHead = (props: ExtendedTableHeadProps) => {
    return (
        <MUITableHead
            sx={{
                fontWeight: '500 !important',
                fontSize: '14px',
                lineHeight: '17px',
                th: {
                    backgroundColor: palette.Surface200,
                    color: palette.TextSecondary,
                    height: '48px',
                },
                '*': {
                    boxSizing: 'border-box !important',
                },
                'th:first-of-type': { pl: props.withSelectCheckbox ? 6 : 4 },
                'th:last-child': { pr: 2 },
                ...(props.sx ? props.sx : ''),
            }}
            {...props}
        >
            {props.children}
        </MUITableHead>
    )
}

const TableFooter = ({ children, sticky }: { sticky?: boolean; children: ReactNode }) => {
    return (
        <Box
            sx={{
                backgroundColor: palette.Surface100,
                borderTop: `1px solid ${palette.Surface500}`,
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                pl: 4,
                pr: 2,
                boxSizing: 'border-box',
                ...(sticky && {
                    position: 'sticky',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: '100%',
                }),
            }}
        >
            {children}
        </Box>
    )
}

const TableBody = (props: TableBodyProps) => {
    return (
        <MUITableBody
            {...props}
            sx={{
                fontWeight: '400',
                ...(props.sx ? props.sx : ''),
            }}
        />
    )
}

const TableCell = (props: CellProps) => {
    const calcCellSize = props.colSize ? (props.colSize / 12) * 100 : 0
    const { colSize, shrink, alignEnd, disabled, sx, children, ...cellProps } = props
    const size = {
        width: shrink ? 'max-content' : colSize ? `${calcCellSize}%` : 'auto',
    }

    return (
        <MUITableCell
            {...cellProps}
            sx={{
                border: 'none',
                py: 0,
                px: 1,
                fontSize: 'inherit',
                fontWeight: 'inherit',
                letterSpacing: 'inherit',
                color: disabled ? palette.TextTertiary : palette.TextPrimary,
                svg: {
                    color: disabled ? palette.TextTertiary : palette.TextPrimary,
                },
                height: props.compact ? '56px' : '64px',
                overflow: 'hidden',
                ...size,
                ...(sx || {}),
            }}
        >
            <Box
                sx={{
                    ...(props.oneLineText
                        ? {
                              ...(alignEnd ? { textAlign: 'right' } : ''),
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              lineClamp: '1',
                          }
                        : {
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center',
                              ...(alignEnd ? { justifyContent: 'flex-end' } : ''),
                          }),
                }}
            >
                {children}
            </Box>
        </MUITableCell>
    )
}

export interface ExtendedTableRowProps extends TableRowProps {
    selected?: boolean
    withSelectCheckbox?: boolean
}

const TableRow = (props: ExtendedTableRowProps) => {
    const { selected, withSelectCheckbox, ...rowProps } = props

    return (
        <MUITableRow
            {...rowProps}
            sx={{
                position: 'relative',
                ...(props.hover ? { cursor: 'pointer' } : ''),
                ...(selected
                    ? {
                          backgroundColor: `${palette.Surface300} !important`,
                      }
                    : {}),
                ...(props.sx ? props.sx : {}),
                'td:first-of-type': { pl: withSelectCheckbox ? 6 : 4 },
                'td:last-child': { pr: 2 },
                borderRadius: '8px',
                // Show checkbox on row hover
                '&:hover .checkbox-show-on-hover': {
                    opacity: 1,
                },
            }}
        />
    )
}

const TableContainer = (props: TableContainerProps) => {
    return (
        <MUITableContainer
            {...props}
            sx={{
                maxHeight: 1,
                ...(props.sx ? props.sx : ''),
            }}
        />
    )
}

const Table = (props: TableProps) => {
    const {
        loading,
        sx,
        empty,
        footer,
        headerCells,
        bodyCells,
        withSelectCheckbox,
        emptyStateText,
        stickyFooter,
    } = props

    const getChildrenLength = (element?: ReactNode): number => {
        if (!isValidElement<{ children?: ReactNode }>(element)) {
            return 0
        }
        return Children.count(element.props.children)
    }

    const [childrenCount, setChildrenCount] = useState<number>(getChildrenLength(bodyCells) || 10)
    const columnCount = getChildrenLength(headerCells)

    useEffect(() => {
        if (!loading && bodyCells) {
            const count = getChildrenLength(bodyCells)
            if (count) {
                setChildrenCount(count)
            }
        }
    }, [bodyCells, loading])

    const Loading = () => {
        return (
            <>
                {Array.from({ length: childrenCount }).map((_, i) => (
                    <TableRow key={i}>
                        {Array.from({ length: columnCount }).map((_, j) => (
                            <TableCell key={j}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '20px',
                                        background: palette.Surface500,
                                        borderRadius: 1 / 2,
                                    }}
                                />
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </>
        )
    }

    const Empty = () => {
        return (
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 3,
                    flexDirection: 'column',
                    background: `Surface100`,
                    pt: 4,
                    pb: 4,
                }}
            >
                <img alt="Empty table" src={staticImagePath(EmptyTable)} width={174} height={120} />
                <Text variant={'h6'}>{emptyStateText?.title ?? `Nothing to show here`}</Text>
                <Text variant={'body2'}>
                    {emptyStateText?.description ??
                        `Try adjusting your filters or search terms to see more.`}
                </Text>
            </Box>
        )
    }

    return (
        <>
            <MUITable
                {...props}
                sx={{
                    fontFamily: 'Helvetica Neue, Arial, sans-serif',
                    fontStyle: 'normal',
                    letterSpacing: '0em',
                    tableLayout: 'auto',
                    fontSize: '14px',
                    lineHeight: '17px',
                    ...(sx || ''),
                }}
            >
                {headerCells && (
                    <TableHead withSelectCheckbox={withSelectCheckbox}>{headerCells}</TableHead>
                )}
                <TableBody>{loading ? <Loading /> : bodyCells}</TableBody>
            </MUITable>
            {footer && !empty && <TableFooter sticky={stickyFooter}>{footer}</TableFooter>}
            {empty && <Empty />}
        </>
    )
}

/**
 * Checkbox cell for multiselect functionality
 * Use as the first cell in a row to enable selection
 */
const TableCheckboxCell = ({
    checked,
    onCheckedChange,
    indeterminate = false,
    disabled = false,
    showOnHover = false,
}: {
    checked: boolean
    onCheckedChange: (checked: boolean) => void
    indeterminate?: boolean
    disabled?: boolean
    /**
     * If true, checkbox is hidden when unchecked and only shows on row hover
     * Always visible when checked or indeterminate
     */
    showOnHover?: boolean
}) => {
    return (
        <Checkbox
            checked={checked}
            indeterminate={indeterminate}
            indeterminateIcon={<IndeterminateCheckBoxIcon />}
            onChange={(e) => {
                e.stopPropagation()
                onCheckedChange(e.target.checked)
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={disabled}
            className={showOnHover && !checked && !indeterminate ? 'checkbox-show-on-hover' : ''}
            sx={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                color: palette.TextSecondary,
                ...(showOnHover && !checked && !indeterminate
                    ? {
                          opacity: 0,
                      }
                    : {}),
            }}
        />
    )
}

Table.TableCell = TableCell
Table.TableRow = TableRow
Table.TableContainer = TableContainer
Table.TableCheckboxCell = TableCheckboxCell
export default Table
